import { useCallback, useEffect, useState } from "react";
import { Shield, Calendar, CheckCircle } from "lucide-react";
import { getMyLifecycle, formatLifecycleError } from "./lifecycleApi";

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-700",
  Submitted: "bg-amber-100 text-amber-800",
  "Notice Period": "bg-blue-100 text-blue-800",
  Clearance: "bg-orange-100 text-orange-800",
  "Final Settlement": "bg-purple-100 text-purple-800",
};

export default function MyExit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getMyLifecycle();
      setData(res.offboarding || null);
    } catch (e) {
      setError(formatLifecycleError(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="w-full max-w-full min-w-0 p-6">
        <p className="text-sm text-slate-500">Loading your exit status…</p>
      </div>
    );
  }

  const byDept = (data?.clearances_list || []).reduce((acc, c) => {
    const d = c.department || "Other";
    if (!acc[d]) acc[d] = [];
    acc[d].push(c);
    return acc;
  }, {});

  return (
    <div className="w-full max-w-full min-w-0 p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">My Exit</h1>
          <p className="text-sm text-slate-500">Track your separation and clearance progress</p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {!data ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="font-medium text-slate-800">No active exit process</p>
          <p className="text-sm text-slate-500 mt-1">You do not have an offboarding in progress.</p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500">Exit status</p>
                <span className={`inline-flex mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[data.status] || "bg-slate-100"}`}>
                  {data.status}
                </span>
              </div>
              {data.last_working_day && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Last working day</p>
                    <p className="font-medium text-slate-800">{data.last_working_day}</p>
                  </div>
                </div>
              )}
            </div>
            {data.separation_type && (
              <p className="text-sm text-slate-600">
                <span className="font-medium">Separation:</span> {data.separation_type}
              </p>
            )}
            <div className="pt-3 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-violet-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Clearance: {data.clearance?.done ?? 0}/{data.clearance?.total ?? 0} complete
                  </p>
                  <div className="mt-2 h-2 w-full max-w-xs bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-500 rounded-full transition-all"
                      style={{
                        width: `${data.clearance?.total ? Math.round((100 * data.clearance.done) / data.clearance.total) : 0}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {Object.keys(byDept).map((dept) => (
            <div key={dept} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50">
                <h2 className="font-semibold text-sm text-slate-800">{dept} clearance</h2>
              </div>
              <div className="divide-y divide-slate-100">
                {byDept[dept].map((c) => (
                  <div key={c.clearance_id} className="px-5 py-3 flex justify-between items-center text-sm">
                    <span className="text-slate-800">{c.item_name}</span>
                    <span
                      className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                        c.status === "Cleared" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {c.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <p className="text-xs text-slate-500 text-center">
            Clearance is completed by HR, IT, and Admin. Contact HR if you have questions.
          </p>
        </>
      )}
    </div>
  );
}
